import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// First-use disclaimer gate. Renders a full-screen, non-dismissible overlay
// on every visitor's first visit until they tap "I Agree." Version-bumping
// the storage key (v1 → v2, etc.) will re-prompt every existing visitor
// once the disclaimer text is materially changed.
const AGREE_KEY = "njc_disclaimer_accepted_v1";

export default function DisclaimerGate() {
    // Start closed on the server / initial paint to avoid flicker; open the
    // gate synchronously in useEffect if no acceptance is on record.
    const [open, setOpen] = useState(false);
    const btnRef = useRef(null);

    useEffect(() => {
        try {
            if (!localStorage.getItem(AGREE_KEY)) setOpen(true);
        } catch (_e) {
            // localStorage unavailable (private mode etc.) — err on the
            // side of showing the disclaimer.
            setOpen(true);
        }
    }, []);

    useEffect(() => {
        if (!open) return;
        // Lock body scroll AND mark #root inert. Because the gate is
        // portalled to document.body (see the createPortal call below),
        // it lives OUTSIDE #root, so a blanket `inert` on #root does not
        // affect the gate itself. Every underlying control (tab-chat,
        // tab-study, phone button, send button, chat input, admin nav,
        // etc.) inherits inert from its #root ancestor and can neither
        // receive focus nor fire click / Enter until the gate unmounts.
        document.body.style.overflow = "hidden";
        const rootEl = document.getElementById("root");
        let previouslyInert = false;
        if (rootEl) {
            previouslyInert = rootEl.hasAttribute("inert");
            if (!previouslyInert) rootEl.setAttribute("inert", "");
        }
        // Force focus to the Agree button on mount.
        setTimeout(() => btnRef.current?.focus(), 0);
        return () => {
            document.body.style.overflow = "";
            if (rootEl && !previouslyInert) rootEl.removeAttribute("inert");
        };
    }, [open]);

    function handleKeyDown(e) {
        // Belt-and-suspenders trap: if Tab or Shift+Tab is pressed inside
        // the dialog, keep focus on the Agree button. `inert` on siblings
        // already prevents focus from escaping, but browsers that don't
        // yet ship `inert` (older Safari builds) rely on this handler.
        if (e.key === "Tab") {
            e.preventDefault();
            btnRef.current?.focus();
        }
        // Explicitly block Escape so the visitor can't dismiss without
        // agreeing (Radix / native dialogs commonly close on Escape).
        if (e.key === "Escape") {
            e.preventDefault();
        }
    }

    function accept() {
        try {
            localStorage.setItem(AGREE_KEY, new Date().toISOString());
        } catch (_e) {
            // ignore
        }
        setOpen(false);
    }

    if (!open) return null;

    return createPortal(
        <div
            data-testid="disclaimer-gate"
            className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8"
            onKeyDown={handleKeyDown}
        >
            <div className="absolute inset-0 bg-[#050B14]/95 backdrop-blur-md" />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="disclaimer-title"
                className="relative codex-card w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden"
            >
                <header className="px-6 pt-6 pb-3 text-center">
                    <div className="font-mono-os text-[10px] tracking-[0.32em] uppercase text-[#D4AF37]/80">
                        Before You Go Any Further…
                    </div>
                    <h2
                        id="disclaimer-title"
                        className="font-serif-display text-xl sm:text-2xl text-[#F9E596] leading-tight mt-2"
                    >
                        The New Jerusalem Codex
                    </h2>
                </header>
                <div className="flex-1 overflow-y-auto scroll-thin px-6 sm:px-10 pb-6">
                    <p
                        data-testid="disclaimer-body"
                        className="font-serif-display text-[15px] sm:text-base leading-[1.75] text-[#E6D9B7] text-center"
                    >
                        This App does not replace the Holy Spirit. Outputs are
                        intended to present truth to prayerfully ponder. The
                        app is a tool to aid discernment but only Jesus Christ
                        is <em className="text-[#F9E596] not-italic font-semibold">THE TRUTH</em> and no machine can ever
                        replace a relationship with Him.{" "}
                        <span className="text-[#F9E596] font-semibold">
                            DO NOT use this tool as your only source of truth
                            or attempt to turn it into an idol.
                        </span>{" "}
                        Please use it for its intended purpose. A tool to
                        present biblical truths and patterns that can help you
                        on your journey with God.
                    </p>
                </div>
                <footer className="px-6 py-5 border-t border-[#D4AF37]/25 bg-[#050B14]/40 flex justify-center">
                    <button
                        ref={btnRef}
                        data-testid="disclaimer-agree-btn"
                        onClick={accept}
                        className="codex-button text-xs sm:text-sm tracking-[0.24em] uppercase px-8 py-3"
                    >
                        I Agree — Enter the Codex
                    </button>
                </footer>
            </div>
        </div>,
        document.body,
    );
}
